// Decode an audio Blob/File/AudioBuffer into mono Float32 at the target sample rate.
//
// Resampling and mixdown go through the browser's OfflineAudioContext so
// the engine's anti-aliasing filter is applied. The previous implementation
// used naive linear interpolation, which on a 48 kHz → 16 kHz drop aliases
// the 8–24 kHz source band into the 0–8 kHz mel range and degrades CTC
// output on real recordings.

async function renderToMono(audioBuffer, targetRate) {
  const Ctor = window.OfflineAudioContext || window.webkitOfflineAudioContext;
  const length = Math.max(1, Math.ceil(audioBuffer.duration * targetRate));
  // Single-channel destination → OfflineAudioContext does the mixdown for us.
  const ctx = new Ctor(1, length, targetRate);
  const src = ctx.createBufferSource();
  src.buffer = audioBuffer;
  src.connect(ctx.destination);
  src.start(0);
  const rendered = await ctx.startRendering();
  return rendered.getChannelData(0).slice();
}

export async function decodeAudio(source, sampleRate) {
  // Fast path: caller handed us decoded PCM that is already mono at the
  // target rate. Avoid spinning up an Offline/Online context (cheap but not
  // free, and gated on a user gesture on Safari/iOS for the online variant).
  if (source instanceof AudioBuffer) {
    if (source.numberOfChannels === 1 && source.sampleRate === sampleRate) {
      return source.getChannelData(0).slice();
    }
    return renderToMono(source, sampleRate);
  }
  const Ctor = window.AudioContext || window.webkitAudioContext;
  // Constructing the AudioContext at the target rate makes decodeAudioData
  // resample as part of decode for almost all common inputs — in which case
  // the post-decode check below short-circuits to the fast path. The
  // renderToMono fallback only kicks in for multichannel inputs or browsers
  // that ignore the requested sampleRate.
  const ctx = new Ctor({ sampleRate });
  try {
    const buffer = await ctx.decodeAudioData(await source.arrayBuffer());
    if (buffer.numberOfChannels === 1 && buffer.sampleRate === sampleRate) {
      return buffer.getChannelData(0).slice();
    }
    return renderToMono(buffer, sampleRate);
  } finally {
    await ctx.close();
  }
}
